import React, { useState, useRef } from "react";
import api from "../../api";

const ContactUs = () => {
  const formRef = useRef();

  const [subject, setSubject] = useState("");
  const [otherSubject, setOtherSubject] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  const handleSubjectChange = (e) => {
    const value = e.target.value;
    setSubject(value);
    if (value !== "other") setOtherSubject("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);

    const form = new FormData(formRef.current);
    const finalSubject = subject === "other" ? otherSubject : subject;

    try {
      await api.post("/contact", {
        name:    form.get("name"),
        email:   form.get("email"),
        subject: finalSubject,
        message: form.get("message"),
      });
      setStatus("success");
      formRef.current.reset();
      setSubject("");
      setOtherSubject("");
    } catch (err) {
      const msg = err.response?.data?.message;
      setStatus(msg || "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className="py-16 sm:py-24 bg-white dark:bg-background-dark text-left"
      id="contact"
    >
      <div className="px-4 md:px-10 lg:px-20 mx-auto max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* LEFT SIDE — Contact Info */}
          <div>
            <h2 className="text-[#181411] dark:text-white text-3xl sm:text-4xl font-bold leading-tight tracking-[-0.015em] mb-6">
              Contact Us
            </h2>
            <p className="text-[#181411]/80 dark:text-white/80 text-lg mb-8">
              We'd love to hear from you! Whether you have a question about our
              products, want to place an order, or explore collaboration
              opportunities.
              <br />
              We're here for you.
            </p>

            <div className="space-y-6">
              {/* Location */}
              <div className="flex items-start">
                <div className="flex-shrink-0 bg-primary/20 p-3 rounded-lg">
                  <svg
                    className="w-6 h-6 text-primary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    ></path>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    ></path>
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-[#181411] dark:text-white">
                    Our Location
                  </h3>
                  <p className="text-[#181411]/70 dark:text-white/70 mt-1">
                    Tunis, laouina 2045
                  </p>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start">
                <div className="flex-shrink-0 bg-primary/20 p-3 rounded-lg">
                  <svg
                    className="w-6 h-6 text-primary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    ></path>
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-[#181411] dark:text-white">
                    Email Us
                  </h3>
                  <p className="text-[#181411]/70 dark:text-white/70 mt-1">
                    pisu.contact@gmail.com
                  </p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start">
                <div className="flex-shrink-0 bg-primary/20 p-3 rounded-lg">
                  <svg
                    className="w-6 h-6 text-primary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    ></path>
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-[#181411] dark:text-white">
                    Call Us
                  </h3>
                  <p className="text-[#181411]/70 dark:text-white/70 mt-1">
                    +216 94 088 487
                  </p>
                </div>
              </div>

              {/* Opening Hours */}
              <div className="flex items-start">
                <div className="flex-shrink-0 bg-primary/20 p-3 rounded-lg">
                  <svg
                    className="w-6 h-6 text-primary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-[#181411] dark:text-white">
                    Opening Hours
                  </h3>
                  <p className="text-[#181411]/70 dark:text-white/70 mt-1">
                    Monday - Friday: 9am - 10pm
                  </p>
                  <p className="text-[#181411]/70 dark:text-white/70">
                    Saturday - Sunday: 10am - 8pm
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE — Contact Form */}
          <div className="bg-background-light dark:bg-background-dark/30 rounded-xl p-6 sm:p-8">
            <h3 className="text-2xl font-bold text-[#181411] dark:text-white mb-6">
              Send Us a Message
            </h3>

            <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Your Name
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  className="w-full px-4 py-3 rounded-lg border bg-white dark:bg-background-dark/50"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Your Email
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 rounded-lg border bg-white dark:bg-background-dark/50"
                />
              </div>

              {/* Subject Dropdown */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Subject
                </label>
                <select
                  name="subject"
                  value={subject}
                  onChange={handleSubjectChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border bg-white dark:bg-background-dark/50"
                >
                  <option value="">Select a topic</option>
                  <option value="Send a Message">Send a Message</option>
                  <option value="Place an Order">Place an Order</option>
                  <option value="Collaboration">Make a Collaboration</option>
                  <option value="Event Order">Order for Party or Event</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Custom Subject Input */}
              {subject === "other" && (
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Specify Subject
                  </label>
                  <input
                    type="text"
                    name="otherSubject"
                    required
                    value={otherSubject}
                    onChange={(e) => setOtherSubject(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border bg-white dark:bg-background-dark/50"
                    placeholder="Type your subject..."
                  />
                </div>
              )}

              {/* Message */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Your Message
                </label>
                <textarea
                  name="message"
                  rows="4"
                  required
                  className="w-full px-4 py-3 rounded-lg border bg-white dark:bg-background-dark/50"
                  placeholder="Tell us what you need..."
                />
              </div>

              {/* Hidden field to send final subject */}
              <input
                type="hidden"
                name="finalSubject"
                value={subject === "other" ? otherSubject : subject}
              />

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-lg bg-primary text-white font-bold flex items-center justify-center hover:bg-primary/90"
              >
                {loading ? "Sending..." : "Send Message"}
              </button>

              {/* Success / Error Messages */}
              {status === "success" && (
                <p className="text-green-600 font-semibold text-sm">
                  Your message has been sent successfully!
                </p>
              )}
              {status && status !== "success" && (
                <p className="text-red-600 font-semibold text-sm">
                  {status === "error"
                    ? "Something went wrong. Please try again."
                    : status}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactUs;
